import React, { useMemo, useState } from 'react';
import { Upload, AlertTriangle, CheckCircle2, XCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { staffApi } from '../lib/api';

const STATUS_TONE = {
  auto: { tone: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'AUTO' },
  review: { tone: 'bg-amber-50 text-amber-700 border-amber-200', icon: AlertTriangle, label: 'REVIEW' },
  no_match: { tone: 'bg-red-50 text-red-700 border-red-200', icon: XCircle, label: 'NON MAPPATO' },
};

/**
 * Wizard import SesameHR.
 * Step 1: Upload CSV
 * Step 2: Preview + correzione mappa Centri → Store
 * Step 3: Conferma e import
 */
export default function SesameImportDialog({ stores, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [defaultStoreId, setDefaultStoreId] = useState('');
  const [loading, setLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  async function handleUpload() {
    if (!file) { toast.error('Seleziona un file CSV'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await staffApi.importSesamePreview(fd);
      setPreview(r.data);
      // Pre-popolo overrides solo per i centri con status 'auto' (gli altri restano vuoti)
      const init = {};
      for (const [centro, info] of Object.entries(r.data.centri_map)) {
        if (info.store_id) init[centro] = info.store_id;
      }
      setOverrides(init);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore preview');
    } finally { setLoading(false); }
  }

  function setOverride(centro, storeId) {
    setOverrides(prev => ({ ...prev, [centro]: storeId || null }));
  }

  // Conta righe pronte all'import dopo i possibili override
  const finalCounts = useMemo(() => {
    if (!preview) return null;
    let create = 0, update = 0, skip = 0;
    for (const r of preview.rows) {
      const centro = r._centro || r.centro;
      const resolved = overrides[centro] || r.store_id_resolved || defaultStoreId;
      if (!resolved) { skip++; continue; }
      if (r.action === 'update') update++;
      else create++;
    }
    return { create, update, skip };
  }, [preview, overrides, defaultStoreId]);

  async function handleCommit() {
    setLoading(true);
    try {
      // Pulisco rows: tengo solo i campi necessari per il commit (no warnings, no UI state)
      const rows = preview.rows.map(r => ({
        _row_idx: r._row_idx,
        _centro: r._centro,
        first_name: r.first_name,
        last_name: r.last_name,
        email_work: r.email_work,
        email_personal: r.email_personal,
        phone_personal: r.phone_personal,
        fiscal_code: r.fiscal_code,
        birth_date: r.birth_date,
        address: r.address,
        status: r.status,
        weekly_hours: r.weekly_hours,
        role_in_store: r.role_in_store,
        store_id_resolved: r.store_id_resolved,
        action: r.action,
      }));
      const r = await staffApi.importSesameCommit({
        rows,
        centri_overrides: overrides,
        default_store_id: defaultStoreId || null,
      });
      setImportResult(r.data);
      setStep(3);
      toast.success(`Import completato: ${r.data.created} creati, ${r.data.updated} aggiornati`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore import');
    } finally { setLoading(false); }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="sesame-import-dialog">
        <DialogHeader>
          <DialogTitle>Importa dipendenti da SesameHR</DialogTitle>
          <DialogDescription>
            Step {step} di 3 — {step === 1 ? 'Carica il file CSV esportato da SesameHR' : step === 2 ? 'Rivedi la mappatura Centro → Negozio' : 'Risultato import'}
          </DialogDescription>
        </DialogHeader>

        {/* STEP 1: Upload */}
        {step === 1 && (
          <div className="space-y-4 py-4" data-testid="step-upload">
            <div className="border-2 border-dashed border-slate-300 rounded-sm p-8 text-center">
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <Label className="text-sm font-medium text-slate-700 cursor-pointer">
                <input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                  data-testid="sesame-file-input"
                />
                <span className="bg-slate-900 text-white px-4 py-2 inline-block rounded-sm hover:bg-slate-700 transition-colors">
                  {file ? file.name : 'Seleziona file CSV'}
                </span>
              </Label>
              <p className="text-xs text-slate-500 mt-3">
                File esportato da SesameHR · max 5MB · UTF-8
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900" data-testid="sesame-report-hint">
              <p className="font-semibold mb-1">Quale report esportare da Sesame?</p>
              <p className="leading-relaxed">
                Per l&apos;import completo dei dati usa il report <strong>&quot;Informazioni complete sui dipendenti&quot;</strong>:
                lo trovi su Sesame nella sezione <strong>&quot;Report&quot;</strong>. Esportalo in CSV e caricalo qui.
              </p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
              <p className="font-semibold mb-1">Cosa succede:</p>
              <ul className="space-y-1 list-disc pl-4">
                <li>Parsing dei campi anagrafici (nome, CF, email, indirizzo, ore, ecc.)</li>
                <li>Match automatico Centro SesameHR → Negozio (RapidFuzz, soglia 80%)</li>
                <li>Check duplicati via codice fiscale (no doppioni)</li>
                <li>Anteprima completa prima di applicare → potrai correggere ogni mapping</li>
              </ul>
            </div>
          </div>
        )}

        {/* STEP 2: Preview + corrections */}
        {step === 2 && preview && (
          <div className="space-y-4 py-2" data-testid="step-preview">
            {preview.format_notice && (
              <div className="bg-amber-50 border border-amber-300 p-3 text-xs text-amber-900" data-testid="sesame-format-notice">
                <p className="font-semibold mb-1 flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Formato rilevato: {preview.format_detected === 'employees_report' ? "'Report sui dipendenti'" : "'Informazioni complete'"}
                </p>
                <p className="leading-relaxed">{preview.format_notice}</p>
              </div>
            )}
            {preview.format_detected === 'employees_report' && (
              <div className={`p-3 border-2 ${!defaultStoreId ? 'border-red-400 bg-red-50' : 'border-emerald-400 bg-emerald-50'}`}>
                <label className={`text-[10px] uppercase tracking-wider font-semibold block mb-1.5 ${!defaultStoreId ? 'text-red-700' : 'text-emerald-700'}`}>
                  Store di default per tutti i dipendenti * {!defaultStoreId ? '— obbligatorio' : '— ok'}
                </label>
                {!defaultStoreId && (
                  <p className="text-xs font-semibold text-red-700 mb-2 flex items-center gap-1.5" data-testid="sesame-default-store-required">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    Questo export Sesame non contiene il negozio: scegli qui sotto lo store a cui assegnare i dipendenti, altrimenti l&apos;import resta a 0.
                  </p>
                )}
                <Select value={defaultStoreId || 'none'} onValueChange={(v) => setDefaultStoreId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="rounded-none bg-white" data-testid="sesame-default-store">
                    <SelectValue placeholder="Scegli uno store…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nessuno (salta tutte le righe) —</SelectItem>
                    {[...stores].sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '', 'it')).map(s => (
                      <SelectItem key={s.store_id} value={s.store_id} data-testid={`sesame-default-store-opt-${s.store_id}`}>
                        {s.display_name || s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  Tutti i dipendenti importati verranno assegnati a questo store. Potrai spostarli individualmente dalla pagina Staff dopo l&apos;import.
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Stat label="Righe nel file" value={preview.total_rows} />
              <Stat label="Da creare" value={finalCounts?.create || 0} tone="emerald" />
              <Stat label="Da aggiornare" value={finalCounts?.update || 0} tone="blue" />
              <Stat label="Saltati" value={finalCounts?.skip || 0} tone="red" />
            </div>

            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                Mappatura Centri → Negozi ({Object.keys(preview.centri_map).length})
              </p>
              <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {Object.entries(preview.centri_map).map(([centro, info]) => {
                  const cfg = STATUS_TONE[info.status] || STATUS_TONE.no_match;
                  const Icon = cfg.icon;
                  const currentValue = overrides[centro] || '';
                  return (
                    <div key={centro} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`centro-row-${centro.replace(/\W+/g, '-')}`}>
                      <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${cfg.tone}`}>
                        <Icon className="w-3 h-3" /> {cfg.label} {info.confidence}%
                      </span>
                      <div className="flex-1 min-w-[200px]">
                        <p className="text-sm font-medium text-slate-900">{centro}</p>
                        <p className="text-[11px] text-slate-500">
                          Suggerito: {info.store_name || '— nessun match —'}
                        </p>
                      </div>
                      <Select value={currentValue || 'none'} onValueChange={(v) => setOverride(centro, v === 'none' ? null : v)}>
                        <SelectTrigger className="w-56 h-9 text-xs" data-testid={`override-${centro.replace(/\W+/g, '-')}`}>
                          <SelectValue placeholder="Seleziona store…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Salta (skip dipendenti) —</SelectItem>
                          {stores.map(s => (
                            <SelectItem key={s.store_id} value={s.store_id}>{s.display_name || s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            </div>

            <details className="bg-slate-50 border border-slate-200 rounded-sm">
              <summary className="cursor-pointer p-3 text-xs uppercase tracking-wide text-slate-600 font-semibold">
                Anteprima primi 5 dipendenti
              </summary>
              <div className="border-t border-slate-200 divide-y divide-slate-100">
                {preview.rows.slice(0, 5).map((r, i) => (
                  <div key={r.fiscal_code || r._row_idx || `prev-${i}`} className="p-3 text-xs flex items-start gap-3">
                    <div className="flex-1">
                      <p className="font-semibold text-slate-900">{r.first_name} {r.last_name}</p>
                      <p className="text-slate-500">{r.role_in_store} · CF: {r.fiscal_code || '—'} · {r.email_work || 'no email'}</p>
                      <p className="text-slate-500">Centro: {r._centro} · {r.weekly_hours ? `${r.weekly_hours}h/sett` : 'ore non specificate'}</p>
                    </div>
                  </div>
                ))}
              </div>
            </details>

            {preview.rows.some(r => r.warnings?.length > 0) && (
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-800">
                <AlertTriangle className="w-4 h-4 inline mr-1.5" />
                {preview.rows.filter(r => r.warnings?.length).length} righe con avvisi. I dipendenti con Centro non mappato verranno saltati. Tutti i campi `hire_date`, `iban`, `ccnl_level`, `contract_type` resteranno vuoti (non presenti in SesameHR).
              </div>
            )}
          </div>
        )}

        {/* STEP 3: Result */}
        {step === 3 && importResult && (
          <div className="space-y-4 py-4" data-testid="step-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-6 text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
              <p className="font-bold text-lg text-emerald-900">Import completato</p>
              <p className="text-sm text-emerald-800 mt-1">
                <strong>{importResult.created}</strong> dipendenti creati · <strong>{importResult.updated}</strong> aggiornati · {importResult.skipped} saltati
              </p>
            </div>
            {importResult.errors?.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-sm p-3">
                <p className="text-xs font-semibold text-red-800 mb-1">Errori ({importResult.errors.length}):</p>
                <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                  {importResult.errors.map((e, i) => (
                    <li key={`err-${e.row ?? i}-${e.name || ''}`}>Riga {e.row} — {e.name}: {e.reason}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-xs text-slate-600">
              <p className="font-semibold mb-1">Prossimi passi consigliati:</p>
              <ul className="space-y-0.5 list-disc pl-4">
                <li>Compila <code className="font-mono">hire_date</code> dipendente per dipendente (per attivare achievement anniversari)</li>
                <li>Quando avrai contratti/IBAN/livello CCNL, fai un secondo import o compila manualmente</li>
                <li>Da `/staff` puoi invitare gli account login (1 click per persona)</li>
              </ul>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 && (
            <>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={handleUpload} disabled={!file || loading} className="gap-1.5" data-testid="sesame-upload-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                Analizza file
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              {preview?.format_detected === 'employees_report' && !defaultStoreId && (
                <span className="text-xs text-red-600 font-medium mr-auto self-center" data-testid="sesame-commit-hint">
                  ← Seleziona lo &quot;Store di default&quot; in alto per abilitare l&apos;import
                </span>
              )}
              <Button variant="outline" onClick={() => setStep(1)}>Indietro</Button>
              <Button onClick={handleCommit} disabled={loading || (finalCounts?.create || 0) + (finalCounts?.update || 0) === 0} className="gap-1.5" data-testid="sesame-commit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Importa {(finalCounts?.create || 0) + (finalCounts?.update || 0)} dipendenti
              </Button>
            </>
          )}
          {step === 3 && (
            <Button onClick={() => { onImported(importResult); onClose(); }} data-testid="sesame-done-btn">
              Chiudi
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, tone }) {
  const tones = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`border rounded-sm p-3 ${tones[tone] || 'bg-slate-50 border-slate-200 text-slate-700'}`}>
      <p className="text-[10px] uppercase tracking-wide font-semibold">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
